import { useState, useRef, type ChangeEvent, type DragEvent } from 'react';
import { bulkImportData, type BulkImportResult } from '../api/client';
import { Icon, type IconName } from '../components/Icon';


type EntityType = 'employees' | 'attendance' | 'invoices' | 'assets' | 'candidates';

interface EntityConfig {
  key: EntityType;
  title: string;
  subtitle: string;
  icon: IconName;
  permission?: string;
  sampleHeaders: string[];
  sampleRows: string[][];
  description: string;
}

const ENTITY_CONFIGS: EntityConfig[] = [
  {
    key: 'employees',
    title: 'Workforce & Employees',
    subtitle: 'Staff profiles, departments & salary structures',
    icon: 'employees',
    permission: 'employee.view',
    description: 'Bulk provision staff records, department cost center links, and initial compensation structures.',
    sampleHeaders: ['first_name', 'last_name', 'employee_code', 'work_email', 'phone', 'designation', 'department', 'date_of_joining', 'base_salary'],
    sampleRows: [
      ['Rohan', 'Verma', 'EMP-2026-101', 'rohan.v@company.com', '+91-9876543210', 'Senior Software Engineer', 'Engineering', '2026-08-01', '95000'],
      ['Sunita', 'Nair', 'EMP-2026-102', 'sunita.n@company.com', '+91-9811223344', 'HR Operations Lead', 'Operations', '2026-08-05', '82000'],
      ['Dev', 'Kashyap', 'EMP-2026-103', 'dev.k@company.com', '+91-9712345678', 'Financial Analyst', 'Finance', '2026-08-10', '78000'],
    ],
  },
  {
    key: 'attendance',
    title: 'Attendance & Swipes',
    subtitle: 'Daily biometric logs & shift punches',
    icon: 'attendance',
    description: 'Import biometric swipe logs, check-in/out timestamps, and daily attendance records.',
    sampleHeaders: ['employee_code', 'date', 'clock_in', 'clock_out', 'status', 'notes'],
    sampleRows: [
      ['ABNY-0001', '2026-08-15', '09:15:00', '18:30:00', 'present', 'Biometric Gate 1 swipe'],
      ['ABNY-0002', '2026-08-15', '09:30:00', '18:45:00', 'present', 'Mobile GPS punch verified'],
    ],
  },
  {
    key: 'invoices',
    title: 'Invoices & Vendor Bills',
    subtitle: 'AP/AR financial accounting ledgers',
    icon: 'finance',
    permission: 'organization.manage',
    description: 'Batch ingest customer receivable invoices or vendor payable bills into the financial ledger.',
    sampleHeaders: ['invoice_number', 'invoice_type', 'party_name', 'issue_date', 'due_date', 'subtotal', 'tax_amount', 'total_amount', 'status'],
    sampleRows: [
      ['INV-2026-801', 'customer_invoice', 'Acme Global Technologies', '2026-08-01', '2026-08-31', '450000', '81000', '531000', 'issued'],
      ['BILL-2026-402', 'vendor_bill', 'Cloudways Infrastructure', '2026-08-01', '2026-08-31', '35000', '6300', '41300', 'issued'],
    ],
  },
  {
    key: 'assets',
    title: 'Physical Assets & IT',
    subtitle: 'Laptops, hardware devices & inventory',
    icon: 'assets',
    permission: 'assets.manage',
    description: 'Bulk register enterprise laptops, testing phones, monitors, and assigned equipment.',
    sampleHeaders: ['asset_code', 'category', 'brand', 'model', 'serial_number', 'purchase_date', 'purchase_cost', 'condition', 'status'],
    sampleRows: [
      ['AST-MBP-01', 'Laptop', 'Apple', 'MacBook Pro 14 M3', 'C02G1234ABCD', '2026-07-15', '199900', 'new', 'in_stock'],
      ['AST-DELL-02', 'Monitor', 'Dell', 'Ultrasharp 27 4K', 'DL987654321', '2026-07-20', '38500', 'good', 'in_stock'],
    ],
  },
  {
    key: 'candidates',
    title: 'Recruitment & ATS',
    subtitle: 'Candidate profiles & pipeline applicants',
    icon: 'recruitment',
    permission: 'recruitment.manage',
    description: 'Import resumes, candidate portfolios, and applicants directly into the ATS pipeline.',
    sampleHeaders: ['job_code', 'first_name', 'last_name', 'email', 'phone', 'current_company', 'expected_ctc', 'notice_period_days', 'stage'],
    sampleRows: [
      ['REQ-ENG-2026-01', 'Ankit', 'Mehra', 'ankit.mehra@applicant.test', '+91-9988776655', 'Flipkart', '2400000', '30', 'applied'],
      ['REQ-ENG-2026-01', 'Sneha', 'Roy', 'sneha.roy@applicant.test', '+91-9877665544', 'Razorpay', '2800000', '15', 'screening'],
    ],
  },
];

export function BulkImport() {
  const [selectedEntity, setSelectedEntity] = useState<EntityType>('employees');
  const [parsedRows, setParsedRows] = useState<Record<string, any>[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentConfig = ENTITY_CONFIGS.find((c) => c.key === selectedEntity)!;

  function parseCSV(text: string) {
    const lines = text.trim().split(/\r\n|\n/).filter(Boolean);
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map((h) => h.trim().replace(/^["']|["']$/g, ''));
    const rows: Record<string, any>[] = [];

    for (let i = 1; i < lines.length; i++) {
      const currentLine = lines[i].split(',').map((c) => c.trim().replace(/^["']|["']$/g, ''));
      if (currentLine.length === 0 || (currentLine.length === 1 && !currentLine[0])) continue;

      const rowObj: Record<string, any> = {};
      headers.forEach((header, idx) => {
        rowObj[header] = currentLine[idx] ?? '';
      });
      rows.push(rowObj);
    }
    return rows;
  }

  function handleFileProcess(file: File) {
    setFileName(file.name);
    setResult(null);
    setMsg(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        if (file.name.endsWith('.json')) {
          const json = JSON.parse(content);
          const rows = Array.isArray(json) ? json : json.rows || [json];
          setParsedRows(rows);
          setMsg({ type: 'success', text: `Loaded ${rows.length} records from JSON file.` });
        } else {
          const rows = parseCSV(content);
          setParsedRows(rows);
          setMsg({ type: 'success', text: `Loaded ${rows.length} records from CSV file.` });
        }
      } catch (err: any) {
        setMsg({ type: 'error', text: `Failed to parse file: ${err.message}` });
      }
    };
    reader.readAsText(file);
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files[0]) {
      handleFileProcess(e.target.files[0]);
    }
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileProcess(e.dataTransfer.files[0]);
    }
  }

  function downloadSampleCSV() {
    const headerLine = currentConfig.sampleHeaders.join(',');
    const rowLines = currentConfig.sampleRows.map((r) => r.join(',')).join('\n');
    const csvContent = `${headerLine}\n${rowLines}`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `sample_${selectedEntity}_template.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async function handleExecuteImport() {
    if (parsedRows.length === 0) return;
    try {
      setImporting(true);
      setMsg(null);
      const res = await bulkImportData(selectedEntity, parsedRows);
      setResult(res.data);
      if (res.data.insertedCount > 0) {
        setMsg({
          type: 'success',
          text: `Successfully ingested ${res.data.insertedCount} records into the database! (${res.data.skippedCount} skipped)`,
        });
      } else {
        setMsg({
          type: 'error',
          text: `0 records inserted. ${res.data.skippedCount} records had validation issues.`,
        });
      }
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Bulk import failed.' });
    } finally {
      setImporting(false);
    }
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <span className="eyebrow">
            <Icon name="database" size="xs" />
            <span>Database Ingestion Engine</span>
          </span>
          <h1>Bulk Data Import Studio</h1>
        </div>
        <button className="btn-quiet" onClick={downloadSampleCSV}>
          <Icon name="download" size="xs" />
          <span>Download Sample CSV Template</span>
        </button>
      </div>

      {msg && <div className={`banner ${msg.type}`}>{msg.text}</div>}

      {/* Entity Selector Grid */}
      <div className="import-card-grid">
        {ENTITY_CONFIGS.map((c) => {
          const isSelected = c.key === selectedEntity;
          return (
            <button
              key={c.key}
              type="button"
              className={`import-entity-btn ${isSelected ? 'active' : ''}`}
              onClick={() => {
                setSelectedEntity(c.key);
                setParsedRows([]);
                setFileName(null);
                setResult(null);
              }}
            >
              <div className="import-icon">
                <Icon name={c.icon} size="md" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>{c.title}</div>
                <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {c.subtitle}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Upload Dropzone Container */}
      <div className="panel" style={{ padding: 24, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0 }}>{currentConfig.title} Ingestion</h3>
            <p className="muted" style={{ margin: '4px 0 0 0', fontSize: 12.5 }}>
              {currentConfig.description}
            </p>
          </div>
          <button className="btn-quiet btn-sm" onClick={downloadSampleCSV}>
            <Icon name="fileSpreadsheet" size="xs" />
            <span>{currentConfig.title} Template</span>
          </button>
        </div>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".csv,.json,text/csv,application/json"
          style={{ display: 'none' }}
        />

        <div
          className={`dropzone-container ${isDragOver ? 'drag-over' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
        >
          <div className="dropzone-icon">
            <Icon name="upload" size="xl" />
          </div>
          <div>
            <strong style={{ fontSize: 15, color: 'var(--ink)' }}>
              {fileName ? fileName : 'Choose a CSV / JSON file or drag and drop here'}
            </strong>
            <p className="muted" style={{ fontSize: 12, margin: '4px 0 0 0' }}>
              Standard CSV with comma delimiters or JSON array format
            </p>
          </div>
        </div>
      </div>

      {/* Parsed Records Table & Execution Panel */}
      {parsedRows.length > 0 && (
        <div className="panel">
          <div className="panel-head">
            <div>
              <strong>Parsed Batch Preview ({parsedRows.length} Rows Detected)</strong>
              <span className="muted" style={{ display: 'block', fontSize: 11.5, marginTop: 2 }}>
                Review normalized fields prior to database batch commit.
              </span>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="btn-quiet btn-sm"
                onClick={() => {
                  setParsedRows([]);
                  setFileName(null);
                  setResult(null);
                }}
              >
                Clear File
              </button>
              <button
                className="btn-primary"
                onClick={() => void handleExecuteImport()}
                disabled={importing}
              >
                <Icon name="checkCircle" size="xs" />
                <span>{importing ? 'Committing Batch…' : `Ingest ${parsedRows.length} Records to DB`}</span>
              </button>
            </div>
          </div>

          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  {Object.keys(parsedRows[0] || {}).map((header) => (
                    <th key={header}>{header.replace(/_/g, ' ').toUpperCase()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parsedRows.slice(0, 10).map((row, idx) => (
                  <tr key={idx}>
                    <td className="mono muted">{idx + 1}</td>
                    {Object.keys(parsedRows[0] || {}).map((header) => (
                      <td key={header} className="mono" style={{ fontSize: 12 }}>
                        {String(row[header] || '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {parsedRows.length > 10 && (
            <div style={{ padding: '10px 16px', background: 'var(--bg-field)', borderTop: '1px solid var(--border-subtle)', fontSize: 12, color: 'var(--ink-muted)', textAlign: 'center' }}>
              Showing first 10 rows of {parsedRows.length} total rows.
            </div>
          )}
        </div>
      )}

      {/* Execution Results Summary */}
      {result && (
        <div className="panel" style={{ marginTop: 20 }}>
          <div className="panel-head">
            <strong>Ingestion Execution Report</strong>
            <span className={`badge ${result.insertedCount > 0 ? 'badge-verified' : 'badge-rejected'}`}>
              {result.insertedCount > 0 ? 'Completed' : 'Failed'}
            </span>
          </div>
          <div style={{ padding: 20 }}>
            <div className="stats-grid" style={{ marginBottom: 16 }}>
              <div className="stat-card">
                <span className="stat-label">Total In Batch</span>
                <span className="stat-value">{result.totalRecords}</span>
              </div>
              <div className="stat-card" style={{ borderLeft: '4px solid var(--status-verified)' }}>
                <span className="stat-label">Inserted Records</span>
                <span className="stat-value" style={{ color: 'var(--status-verified)' }}>{result.insertedCount}</span>
              </div>
              <div className="stat-card" style={{ borderLeft: '4px solid var(--status-alert)' }}>
                <span className="stat-label">Skipped / Failed</span>
                <span className="stat-value" style={{ color: 'var(--status-alert)' }}>{result.skippedCount}</span>
              </div>
            </div>

            {result.errors.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 10px 0', color: 'var(--status-alert)' }}>Row Validation Issues ({result.errors.length})</h4>
                <div className="table-responsive">
                  <table style={{ fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 80 }}>Row</th>
                        <th style={{ width: 140 }}>Identifier</th>
                        <th>Error Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((err, idx) => (
                        <tr key={idx}>
                          <td className="mono">#{err.row}</td>
                          <td className="mono font-bold">{err.identifier || '—'}</td>
                          <td style={{ color: 'var(--status-alert)' }}>{err.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
